/**
 * Progress Context - Manages user progress and scoring
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ProgressContext = createContext();

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within ProgressProvider');
  }
  return context;
};

export const ProgressProvider = ({ children }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, getAccessToken } = useAuth();

  const getAuthHeaders = useCallback(() => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getAccessToken]);

  const loadProgress = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API_URL}/api/progress`, {
        headers: getAuthHeaders()
      });
      // Map database fields to frontend format (backend persists to Supabase user_progress)
      const mappedProgress = {
        completedScenarios: response.data.completed_scenarios || [],
        currentScenario: response.data.current_scenario,
        skills: response.data.skills || {
          dataLiteracy: 0,
          stakeholderManagement: 0,
          technicalDepth: 0,
          businessAcumen: 0
        },
        totalScore: response.data.total_score || 0,
        taskCompletions: response.data.task_completions || {},
        checkpoints: response.data.checkpoints || [],
        communicationRatings: response.data.communication_ratings || {}
      };
      setProgress(mappedProgress);
      // Ensure backend has a persisted row (idempotent: same data so no change if already saved)
      const dbProgress = {
        completed_scenarios: mappedProgress.completedScenarios,
        current_scenario: mappedProgress.currentScenario,
        skills: mappedProgress.skills,
        total_score: mappedProgress.totalScore,
        task_completions: mappedProgress.taskCompletions,
        checkpoints: mappedProgress.checkpoints,
        communication_ratings: mappedProgress.communicationRatings
      };
      await axios.post(`${API_URL}/api/progress`, dbProgress, {
        headers: getAuthHeaders()
      }).catch((err) => {
        console.warn('Progress persist check failed (row may still exist):', err?.message);
      });
    } catch (error) {
      console.error('Error loading progress:', error);
      setProgress({
        completedScenarios: [],
        currentScenario: null,
        skills: {
          dataLiteracy: 0,
          stakeholderManagement: 0,
          technicalDepth: 0,
          businessAcumen: 0
        },
        totalScore: 0,
        taskCompletions: {},
        checkpoints: [],
        communicationRatings: {}
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  useEffect(() => {
    if (isAuthenticated) {
      loadProgress();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, loadProgress]);

  const updateProgress = async (newProgress) => {
    if (!isAuthenticated) return;
    
    try {
      // Map frontend format to database format
      const dbProgress = {
        completed_scenarios: newProgress.completedScenarios || [],
        current_scenario: newProgress.currentScenario,
        skills: newProgress.skills || {},
        total_score: newProgress.totalScore || 0,
        task_completions: newProgress.taskCompletions || {},
        checkpoints: newProgress.checkpoints || [],
        communication_ratings: newProgress.communicationRatings || {}
      };
      
      await axios.post(`${API_URL}/api/progress`, dbProgress, {
        headers: getAuthHeaders()
      });
      setProgress(newProgress);
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const updateTaskCompletion = async (scenarioId, taskIndex) => {
    if (!isAuthenticated || !progress) return;
    
    const currentCompletions = progress.taskCompletions || {};
    const scenarioCompletions = currentCompletions[scenarioId] || [];
    
    if (!scenarioCompletions.includes(taskIndex)) {
      const updatedCompletions = {
        ...currentCompletions,
        [scenarioId]: [...scenarioCompletions, taskIndex].sort((a, b) => a - b)
      };
      
      const updatedProgress = {
        ...progress,
        taskCompletions: updatedCompletions
      };
      
      await updateProgress(updatedProgress);
    }
  };

  const addCheckpoint = async (checkpoint) => {
    if (!isAuthenticated || !progress) return;
    
    const updatedProgress = {
      ...progress,
      checkpoints: [...(progress.checkpoints || []), checkpoint]
    };
    
    await updateProgress(updatedProgress);
  };

  const completeScenario = async (scenarioId, score, portfolioArtifact = null, uploads = []) => {
    const updatedProgress = {
      ...progress,
      completedScenarios: [...(progress.completedScenarios || []), {
        id: scenarioId,
        completedAt: new Date().toISOString(),
        score: score,
        portfolioArtifact: portfolioArtifact,
        uploads: uploads
      }],
      skills: {
        dataLiteracy: (progress.skills?.dataLiteracy || 0) + (score.skills?.dataLiteracy ?? score.dataLiteracy ?? 0),
        stakeholderManagement: (progress.skills?.stakeholderManagement || 0) + (score.skills?.stakeholderManagement ?? score.stakeholderManagement ?? 0),
        technicalDepth: (progress.skills?.technicalDepth || 0) + (score.skills?.technicalDepth ?? score.technicalDepth ?? 0),
        businessAcumen: (progress.skills?.businessAcumen || 0) + (score.skills?.businessAcumen ?? score.businessAcumen ?? 0)
      },
      totalScore: (progress.totalScore || 0) + (score.total || 0)
    };
    setProgress(updatedProgress);
    try {
      await updateProgress(updatedProgress);
    } catch (err) {
      console.error('Failed to persist scenario completion to database:', err);
    }
  };

  return (
    <ProgressContext.Provider value={{
      progress,
      loading,
      loadProgress,
      updateProgress,
      completeScenario,
      updateTaskCompletion,
      addCheckpoint,
      getAuthHeaders
    }}>
      {children}
    </ProgressContext.Provider>
  );
};
